// =====================================================================
// Assessment Center — Student Panel
// Results page — shows real grading state per the grading rules:
//   - Multiple Choice exam → full score shown immediately.
//   - Essay / Mixed exam   → MC answers show correct/incorrect right
//     away; essay answers show "Waiting for review" until an
//     instructor completes it; the final grade banner only appears
//     once every essay on the attempt has been reviewed.
// =====================================================================
import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Badge, Card, PageHeader, Spinner, statusTone } from "../../../shared/components/ui";
import { getResult, type AttemptResult, type ResultAnswer } from "../lib/mock-api";

function AnswerStatusBadge({ a }: { a: ResultAnswer }) {
  if (!a.answered) return <Badge tone="neutral">Not answered</Badge>;
  if (a.type === "multiple_choice") {
    return a.is_correct ? (
      <Badge tone="green">Correct · {a.points_awarded}/{a.points_possible} pts</Badge>
    ) : (
      <Badge tone="red">Incorrect · 0/{a.points_possible} pts</Badge>
    );
  }
  // essay
  if (a.review_state === "completed") {
    return (
      <Badge tone="navy">
        Reviewed · {a.points_awarded}/{a.points_possible} pts
      </Badge>
    );
  }
  return <Badge tone="amber">Waiting for review</Badge>;
}

export default function ResultsPage() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const location = useLocation();
  const autoSubmitted = Boolean((location.state as { autoSubmitted?: boolean } | null)?.autoSubmitted);

  const [result, setResult] = useState<AttemptResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!attemptId) return;
    getResult(attemptId)
      .then(setResult)
      .catch((e) => setError(e instanceof Error ? e.message : "Couldn't load this result."))
      .finally(() => setLoading(false));
  }, [attemptId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-20 text-slate-500">
        <Spinner /> Loading results…
      </div>
    );
  }

  if (error || !result) {
    return <p className="text-sm text-red-600">{error ?? "Result not found."}</p>;
  }

  const totalQuestions = result.sections.reduce((n, s) => n + s.answers.length, 0);
  const answeredCount = result.sections.reduce((n, s) => n + s.answers.filter((a) => a.answered).length, 0);
  const pendingEssayCount = result.sections.reduce(
    (n, s) => n + s.answers.filter((a) => a.type === "essay" && a.review_state === "pending").length,
    0
  );
  const isFullyGraded = result.attempt.status === "graded" && result.grade !== null;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Results" subtitle={result.exam.title} />

      <Card className="mb-6 flex flex-wrap items-center justify-between gap-3 p-5">
        <div>
          <p className="font-display text-lg font-semibold text-navy-900">
            {autoSubmitted ? "Time's up — your exam was submitted automatically." : "Exam submitted."}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            {answeredCount} of {totalQuestions} questions answered · Attempt #{result.attempt.attempt_number}
          </p>
        </div>
        <Badge tone={statusTone(result.attempt.status)}>
          {isFullyGraded ? "Graded" : "Submitted — awaiting grading"}
        </Badge>
      </Card>

      {isFullyGraded && result.grade ? (
        <Card className="mb-6 flex flex-wrap items-center justify-between gap-4 p-5">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">Final score</p>
            <p className="font-display text-3xl font-semibold text-navy-900">
              {result.grade.total_score} / {result.grade.max_score}
            </p>
            <p className="text-sm text-slate-500">{result.grade.percentage}%</p>
          </div>
          <Badge tone={result.grade.passed ? "green" : "red"}>{result.grade.passed ? "Passed" : "Not passed"}</Badge>
        </Card>
      ) : (
        <div className="mb-6 rounded-lg border border-navy-100 bg-navy-50/60 px-4 py-3 text-sm text-navy-800">
          {pendingEssayCount > 0
            ? `Your final grade is pending — ${pendingEssayCount} essay answer${pendingEssayCount === 1 ? "" : "s"} still ${pendingEssayCount === 1 ? "needs" : "need"} instructor review. Multiple choice answers below are already graded.`
            : "Grading is still in progress."}
        </div>
      )}

      <div className="space-y-6">
        {result.sections.map((section) => (
          <Card key={section.id}>
            <div className="border-b border-border-soft px-5 py-4">
              <h3 className="font-display text-base font-semibold text-navy-900">{section.title}</h3>
            </div>
            <ul className="divide-y divide-border-soft">
              {section.answers.map((a, i) => (
                <li key={a.exam_question_id} className="px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="text-sm font-medium text-navy-900">
                      {i + 1}. {a.prompt}
                    </p>
                    <AnswerStatusBadge a={a} />
                  </div>
                  {a.answered ? (
                    a.type === "multiple_choice" ? (
                      <p className="mt-2 rounded-lg bg-navy-50/60 px-3 py-2 text-sm text-slate-700">
                        Your answer: {a.selected_option_text}
                      </p>
                    ) : (
                      <p className="mt-2 whitespace-pre-wrap rounded-lg bg-navy-50/60 px-3 py-2 text-sm text-slate-700">
                        {a.essay_answer_text}
                      </p>
                    )
                  ) : (
                    <p className="mt-2 text-sm italic text-slate-400">Not answered</p>
                  )}
                  {a.review_state === "completed" && a.reviewer_feedback && (
                    <p className="mt-2 rounded-lg border border-navy-100 bg-white px-3 py-2 text-sm text-navy-800">
                      <span className="font-medium">Instructor feedback: </span>
                      {a.reviewer_feedback}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>

      <div className="mt-6">
        <Link to="/student/assessment-center/my-exams" className="text-sm font-medium text-navy-700 hover:underline">
          ← Back to My Exams
        </Link>
      </div>
    </div>
  );
}
